import { CheckCircle, Clock, Mail, FileDigit, Calendar, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

interface SubmissionConfirmationProps {
  evidenceId: string;
  fileName: string;
  fileHash: string;
  submittedAt: Date;
  contactEmail?: string;
}

export function SubmissionConfirmation({
  evidenceId,
  fileName,
  fileHash,
  submittedAt,
  contactEmail,
}: SubmissionConfirmationProps) {
  // contactEmail is used for email confirmation display below
  const hasContactEmail = Boolean(contactEmail);
  const formattedDate = submittedAt.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const formattedTime = submittedAt.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });

  return (
    <div className="max-w-2xl mx-auto">
      {/* Success Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
          <CheckCircle className="h-8 w-8 text-green-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Evidence Submitted Successfully</h1>
        <p className="mt-2 text-gray-600">
          Your submission has been received and is pending review by court staff.
        </p>
      </div>

      {/* Submission Details Card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
        <div className="px-6 py-4 bg-justice-navy/5 border-b border-gray-200">
          <h2 className="font-semibold text-justice-navy">Submission Receipt</h2>
        </div>

        <div className="p-6 space-y-4">
          {/* Evidence ID */}
          <div className="flex items-start gap-3">
            <FileDigit className="h-5 w-5 text-gray-400 mt-0.5" />
            <div>
              <dt className="text-sm font-medium text-gray-500">Evidence ID</dt>
              <dd className="mt-1 font-mono text-lg text-justice-navy">{evidenceId}</dd>
            </div>
          </div>

          {/* File Name */}
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-gray-400 mt-0.5" />
            <div>
              <dt className="text-sm font-medium text-gray-500">File Name</dt>
              <dd className="mt-1 text-gray-900">{fileName}</dd>
            </div>
          </div>

          {/* Submission Time */}
          <div className="flex items-start gap-3">
            <Calendar className="h-5 w-5 text-gray-400 mt-0.5" />
            <div>
              <dt className="text-sm font-medium text-gray-500">Submitted On</dt>
              <dd className="mt-1 text-gray-900">{formattedDate} at {formattedTime}</dd>
            </div>
          </div>

          {/* Cryptographic Hash */}
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-gray-400 mt-0.5" />
            <div className="flex-1">
              <dt className="text-sm font-medium text-gray-500">SHA-256 Hash (Cryptographic Fingerprint)</dt>
              <dd className="mt-1 font-mono text-xs bg-gray-50 p-2 rounded border border-gray-200 break-all text-gray-700">
                {fileHash}
              </dd>
              <p className="mt-1 text-xs text-gray-500">
                This unique fingerprint verifies your file has not been altered.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Email Confirmation Notice */}
      {hasContactEmail && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
          <div className="flex items-start gap-3">
            <Mail className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-800">Confirmation Sent</h3>
              <p className="mt-1 text-sm text-blue-700">
                A confirmation email has been sent to <strong>{contactEmail}</strong> with your submission details.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Expected Timeline */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
        <div className="flex items-start gap-3">
          <Clock className="h-5 w-5 text-amber-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-amber-800">Expected Review Timeline</h3>
            <p className="mt-1 text-sm text-amber-700">
              Submissions are typically reviewed within <strong>2-3 business days</strong>. 
              You will receive notification when your submission is approved or if additional 
              information is needed.
            </p>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-8">
        <div className="flex items-start gap-3">
          <Mail className="h-5 w-5 text-gray-400 mt-0.5" />
          <div>
            <h3 className="font-semibold text-gray-900">Questions?</h3>
            <p className="mt-1 text-sm text-gray-600">
              If you have questions about your submission, please contact the Court Clerk's Office:
            </p>
            <ul className="mt-2 text-sm text-gray-600 space-y-1">
              <li>Email: <a href="mailto:clerk@court.demo" className="text-justice-navy hover:underline">clerk@court.demo</a></li>
              <li>Phone: (555) 123-4567</li>
              <li>Reference your Evidence ID: <code className="bg-gray-200 px-1 rounded">{evidenceId}</code></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Link
          to="/"
          className="inline-flex items-center justify-center px-6 py-3 bg-justice-navy text-white font-medium rounded-lg hover:bg-justice-navy/90 transition-colors"
        >
          Return to Dashboard
        </Link>
        <Link
          to="/submit"
          className="inline-flex items-center justify-center px-6 py-3 bg-white text-justice-navy font-medium rounded-lg border border-justice-navy hover:bg-justice-navy/5 transition-colors"
        >
          Submit Another
        </Link>
      </div>
    </div>
  );
}
